/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  EuiSelectableOption,
  EuiIcon,
  EuiSelectable,
  EuiPopoverTitle,
  EuiFlexGroup,
  EuiFlexItem,
  EuiButtonGroup,
} from "@inscopix/ideas-eui";
import { ProjectFolderDjango } from "pages/gdt/folders/ProjectFoldersProvider.api";
import { DataTableStoreState } from "pages/gdt/store/DataTableProvider.types";
import { useMemo, useState } from "react";

interface ProjectBreadcrumbsTableProps {
  selectedTable?: DataTableStoreState["tables"][number];
  tables: DataTableStoreState["tables"][number][];
  folders: ProjectFolderDjango[];
  setSelectedTableId: (id: string | undefined) => void;
}

/*
 * Displays a popover to search and navigate to a project table
 */
export const ProjectBreadcrumbsTablePopover = ({
  selectedTable,
  tables,
  folders,
  setSelectedTableId,
}: ProjectBreadcrumbsTableProps) => {
  // track search state
  // this is used to remove the folder names from the search results when searching
  // because of the visual awkwardness of the group labels
  const [isSearching, setIsSearching] = useState(false);
  const [showHidden, setShowHidden] = useState(false);

  // build option list
  const options = useMemo(() => {
    const options: EuiSelectableOption[] = [];
    const tableItems: EuiSelectableOption[] = tables
      .filter((table) => table.folder === null)
      .filter(({ hidden }) => showHidden || !hidden) // filter out hidden tables
      .map((table) => ({
        label: table.name,
        key: table.id,
        checked: table.id === selectedTable?.id ? "on" : undefined,
        prepend: (
          <EuiIcon
            type={
              table.kind === "analysis" ? "indexMapping" : "tableDensityNormal"
            }
          />
        ),
      }));
    for (const folder of folders) {
      const tablesInFolder = tables.filter(
        (table) => table.folder === folder.id,
      );
      if (tablesInFolder.length > 0) {
        options.push(
          {
            label: folder.name,
            key: folder.id,
            isGroupLabel: true,
            // show folder color if available
            css:
              folder.color !== null
                ? css`
                    color: ${folder.color}!important;
                    padding-left: 36px !important;
                  `
                : css`
                    padding-left: 36px !important;
                  `,
            // remove folders from view on search
            // has to be a space character, empty gets ignored
            searchableLabel: " ",
          },
          ...tablesInFolder
            .filter(({ hidden }) => showHidden || !hidden) // filter out hidden tables
            .map((table) => ({
              label: `${table.name} (${table.key})`,
              key: table.id,
              searchableLabel: `${table.name} ${table.key}`,
              checked:
                table.id === selectedTable?.id ? ("on" as const) : undefined,
              prepend: (
                <EuiIcon
                  type={
                    table.kind === "analysis"
                      ? "indexMapping"
                      : "tableDensityNormal"
                  }
                  // indent under folders if folders are displayed (not searching)
                  css={isSearching ? {} : { marginLeft: "20px" }}
                />
              ),
            })),
        );
      }
    }
    options.push(...tableItems);
    return options;
  }, [tables, showHidden, selectedTable?.id, folders, isSearching]);

  return (
    <EuiSelectable
      singleSelection="always"
      options={options}
      searchable
      onChange={(options) => {
        const selectedOption = options.find(
          (option) => option.checked === "on",
        );
        if (selectedOption) {
          const selectedTableId = selectedOption.key;
          setSelectedTableId(selectedTableId);
        }
      }}
      searchProps={{
        placeholder: "Filter tables",
        compressed: true,
        onChange: (searchValue: string) => {
          setIsSearching(searchValue.length > 0);
        },
      }}
      aria-label="Switch to table"
      emptyMessage="No tables available"
      noMatchesMessage="No tables found"
      css={css`
        width: 420px;
      `}
    >
      {(list, search) => (
        <>
          <EuiPopoverTitle paddingSize="s">
            <EuiFlexGroup responsive={false}>
              <EuiFlexItem>{search}</EuiFlexItem>
              <EuiFlexItem
                grow={false}
                css={
                  // fix EUI border radius for single button
                  css`
                    .euiButtonGroupButton {
                      border-radius: 4px !important;
                    }
                  `
                }
              >
                <EuiButtonGroup
                  type="multi"
                  legend="Show/hide hidden tables"
                  options={[
                    {
                      id: "showHidden" as const,
                      label: "Show/hide hidden tables",
                      iconType: "eyeClosed",
                    },
                  ]}
                  idToSelectedMap={{
                    showHidden: showHidden,
                  }}
                  onChange={() => {
                    setShowHidden(!showHidden);
                  }}
                  isIconOnly
                />
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiPopoverTitle>
          {list}
        </>
      )}
    </EuiSelectable>
  );
};
